import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import AdminLayout from "@/components/admin/AdminLayout";
import axios from "axios";
import { Save, Loader2 } from "lucide-react";
import { ADMIN } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminWebhooks() {
  const { authHeader } = useAuth();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings`, { headers: authHeader() })
      .then(r => setSettings(r.data || {}))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, { headers: authHeader() });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {}
    setSaving(false);
  };

  const field = (key, label, placeholder = "", helpText = "") => (
    <div>
      <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
      <input
        value={settings[key] || ""}
        onChange={e => setSettings({ ...settings, [key]: e.target.value })}
        placeholder={placeholder}
        className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
      />
      {helpText && <p className="text-xs text-slate-400 mt-1">{helpText}</p>}
    </div>
  );

  if (loading) return <AdminLayout title="Webhook Settings"><div className="flex items-center justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-blue-600 border-t-transparent animate-spin" /></div></AdminLayout>;

  return (
    <AdminLayout title="Webhook Settings">
      <div data-testid={ADMIN.webhookForm} className="max-w-2xl space-y-6">

        {/* RetellAI Settings */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-1">RetellAI Voice Agent</h3>
          <p className="text-xs text-slate-400 mb-5">Configure AvA's voice call integration</p>
          <div className="space-y-4">
            {field("retell_agent_id", "RetellAI Agent ID", "agent_...", "Your AvA voice agent ID from RetellAI")}
            {field("retell_phone", "RetellAI Phone Number", "+17029838016", "The outbound number AvA will call from")}
            {field("retell_webhook_url", "RetellAI Webhook URL", "https://...", "Webhook URL from RetellAI dashboard")}
            {field("retell_webhook_secret", "RetellAI Webhook Secret", "secret...", "For webhook signature verification")}
            {field("voice_cta_label", "Voice CTA Label", "Request a Call from AvA", "Label shown on the call request button")}
          </div>
        </div>

        {/* Automation Webhooks */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-1">Automation Webhooks</h3>
          <p className="text-xs text-slate-400 mb-5">These URLs receive POST requests when lead events occur (new lead, qualified clinic, etc.)</p>
          <div className="space-y-4">
            {field("make_webhook_url", "Make.com Webhook URL", "https://hook.make.com/...")}
            {field("n8n_webhook_url", "n8n Webhook URL", "https://your-n8n.com/webhook/...")}
            {field("crm_webhook_url", "CRM Webhook URL", "https://...")}
            {field("slack_webhook_url", "Slack Notification Webhook", "https://hooks.slack.com/...")}
            {field("google_sheets_webhook_url", "Google Sheets Export Webhook", "https://...")}
            {field("email_api_url", "Email Sending API URL", "https://...")}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-all shadow-md shadow-blue-600/20">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
            {saving ? "Saving..." : "Save Settings"}
          </button>
          {saved && <span className="text-sm text-emerald-600 font-medium">Settings saved</span>}
        </div>

        {/* Webhook Events Reference */}
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-5">
          <h4 className="text-xs font-semibold text-slate-700 mb-3 uppercase tracking-wide">Webhook Events Reference</h4>
          <div className="space-y-1.5 text-xs text-slate-500">
            {[
              "new_lead — New lead submitted via AvA",
              "qualified_clinic — Lead qualified as a clinic",
              "vendor_partner — Lead qualified as vendor/pharmacy partner",
              "ruo_risk — Research-use-only concern flagged",
              "live_handoff — Status changed to Live Team Handoff",
            ].map((e, i) => <p key={i}><code className="bg-white px-1.5 py-0.5 rounded border border-slate-200 text-xs">{e}</code></p>)}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
